// Drawing the poster itself: treated image full bleed, type slammed on top.
//
// One renderer, called twice for different reasons. The composer calls it
// every time a knob moves; the save calls it once at the size the world
// wants. Everything is a fraction of the canvas, so the only difference
// between the two is resolution.
//
// Picture always fills the frame, and the type is set by hand: face,
// colour, size, where it sits. Presets went because they were making the
// decisions that are the actual design, and every one of them leaned on a
// shadow, a keyline or a dark wash to stay readable. Those are crutches. A
// flyer picks a colour that works against the picture, or it picks a
// different picture.
//
// The house move that stays is fit-to-measure: the title is scaled until it
// spans its width, so the words make the block rather than sitting in one.
// Size moves that width in rather than dropping to a smaller face.

import { treatPixels, PALETTES } from './treat.js'

// The two shapes in the world. Posters are the 2:3 sheets on walls,
// billboards the 2:1 boards. Both are authored here so the same picture
// can go up as either without being re-cropped by hand.
export const FORMATS = {
  poster: { label: 'poster', w: 512, h: 768, lines: 3, textTop: 0.60, maxTitle: 0.17 },
  // 1024x570 rather than a clean 2:1. The screen meshes in adPosters.glb
  // measure 11.93 x 6.64 and 9.833 x 5.472, both 1.797, and the texture is
  // mapped straight onto them, so anything else stretches the art. Sam
  // measured 717x402 (1.784); the mesh is what the pixels land on, and the
  // two are 0.7% apart, which is nothing.
  billboard: { label: 'billboard', w: 1024, h: 570, lines: 2, textTop: 0.46, maxTitle: 0.26 },
}
export const POSTER_W = FORMATS.poster.w
export const POSTER_H = FORMATS.poster.h

const INK = '#1b1636'

// The letterforms. The HUD face is rounded and friendly, which is exactly
// wrong on a flyer at any weight, so the poster gets its own faces and
// nothing else on the site uses them.
//
// Both display cuts ship one weight only. Asking for 900 would make the
// browser synthesise a fake bold, which smears the outline style and is
// why the weight travels with the face rather than being hardcoded.
export const FACES = {
  condensed: {
    label: 'condensed',
    hint: 'tall and tight. the one on the wall outside the club',
    varName: '--font-poster-condensed',
    stack: 'Impact, Haettenschweiler, sans-serif',
    weight: 400,
    tracking: -0.012,
  },
  block: {
    label: 'block',
    hint: 'wide and heavy. it does not care what is behind it',
    varName: '--font-poster-block',
    stack: '"Arial Black", "Helvetica Neue", sans-serif',
    weight: 400,
    tracking: -0.02,
  },
  warped: {
    label: 'warped',
    hint: 'wrong on purpose. the letters have had a night out',
    varName: '--font-poster-warped',
    stack: '"Arial Black", sans-serif',
    weight: 800,
    tracking: -0.005,
  },
  plain: {
    label: 'plain',
    hint: 'the town\'s own face, for things that are not a rave',
    varName: '--font-rounded',
    stack: 'system-ui, sans-serif',
    weight: 800,
    tracking: 0.02,
  },
}

// Where the words sit. Split puts the title up top and the details down at
// the bottom, which is the oldest trick on a flyer and still the best one
// for letting the middle of the picture be the picture.
export const POSITIONS = {
  bottom: { label: 'bottom' },
  top: { label: 'top' },
  split: { label: 'split' },
}

/** What the type may be set in: neutrals, then whatever the palette is doing. */
export function inkOptions(palette) {
  const stops = (PALETTES[palette] || PALETTES.dusk).stops
  return [
    { id: 'white', value: '#ffffff' },
    { id: 'cream', value: '#f7e7d2' },
    { id: 'ink', value: '#141028' },
    ...stops.slice(1).map((value, i) => ({ id: `stop${i}`, value })),
  ]
}

/**
 * Resolve a face to a canvas font family, using the page's loaded variable.
 *
 * next/font declares its variables on the element carrying the generated
 * class, and layout.jsx puts those on <body>, not <html>. Reading the root
 * returns an empty string and silently falls through to the stack, which is
 * how the first cut of this ended up setting Impact everywhere while Anton
 * sat loaded and unused.
 */
export function faceFamily(id) {
  const face = FACES[id] || FACES.condensed
  if (typeof document === 'undefined') return face.stack
  const root = document.body || document.documentElement
  const v = getComputedStyle(root).getPropertyValue(face.varName).trim()
    || getComputedStyle(document.documentElement).getPropertyValue(face.varName).trim()
  return v ? `${v}, ${face.stack}` : face.stack
}

/**
 * Canvas cannot draw with a face the document has not loaded, and next/font
 * loads on demand: the first paint would silently fall back to Impact and
 * only correct itself on some later redraw. Ask for them up front and let
 * the caller redraw when they land.
 */
export function loadFaces() {
  if (typeof document === 'undefined' || !document.fonts) return Promise.resolve()
  const wanted = Object.keys(FACES).map((id) => {
    const f = FACES[id]
    return document.fonts.load(`${f.weight} 100px ${faceFamily(id)}`).catch(() => null)
  })
  return Promise.all(wanted).then(() => document.fonts.ready)
}

/** Split words into n lines of roughly equal length, keeping their order. */
function balance(words, n) {
  if (n <= 1) return [words.join(' ')]
  const total = words.join('').length
  const target = total / n
  const lines = []
  let line = []
  let run = 0
  for (let i = 0; i < words.length; i++) {
    line.push(words[i])
    run += words[i].length
    const left = words.length - i - 1
    // Leave enough words for the remaining lines, and break once this one
    // has had its share.
    if (lines.length < n - 1 && run >= target && left >= n - lines.length - 1) {
      lines.push(line.join(' '))
      line = []
      run = 0
    }
  }
  if (line.length) lines.push(line.join(' '))
  while (lines.length < n) lines.push('')
  return lines.filter(Boolean)
}

/** Font size at which this text spans exactly `width`. */
function fitSize(ctx, text, width, font, weight) {
  if (!text) return 0
  ctx.font = `${weight} 100px ${font}`
  const at100 = ctx.measureText(text).width
  if (!at100) return 0
  return (width / at100) * 100
}

/**
 * Choose how many lines the title wants.
 *
 * Fewer lines means bigger type, which is what a flyer wants, but one very
 * long word set across the whole width leaves a block two pixels tall. So
 * try each count and keep the one whose smallest line is largest, subject
 * to the block fitting the space it has.
 */
function layOutTitle(ctx, title, width, maxBlockH, font, weight, maxLines, maxSize = Infinity) {
  const words = String(title || '').toUpperCase().split(/\s+/).filter(Boolean)
  if (!words.length) return []
  // Two words on a 2:1 board fit to a size that leaves no board. The cap
  // is what stops a short title eating the picture; the line simply stops
  // short of the measure, which is what a typesetter would do anyway.
  const fit = (l) => ({ text: l, size: Math.min(fitSize(ctx, l, width, font, weight), maxSize) })
  let best = null
  for (let n = 1; n <= Math.min(maxLines, words.length); n++) {
    const lines = balance(words, n)
    const sized = lines.map(fit)
    const blockH = sized.reduce((sum, l) => sum + l.size * 0.86, 0)
    if (blockH > maxBlockH) continue
    const smallest = Math.min(...sized.map((l) => l.size))
    if (!best || smallest > best.smallest) best = { sized, smallest }
  }
  // Everything overflowed (one enormous word): take the most lines and let
  // the block be short rather than drawing nothing.
  if (!best) return balance(words, Math.min(maxLines, words.length)).map(fit)
  return best.sized
}

/** Cream on a dark bar, ink on a light one. The bar colour is the choice. */
function pickContrast(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ''))
  if (!m) return '#141028'
  const n = parseInt(m[1], 16)
  const l = 0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)
  return l < 140 ? '#f7e7d2' : '#141028'
}

function setTracking(ctx, px) {
  // Recent browsers only. Where it is missing the type is simply not
  // tracked out, which is a smaller loss than not drawing.
  try { ctx.letterSpacing = `${px}px` } catch { /* older engine */ }
}

/**
 * Draw a whole poster.
 *
 * @param ctx     2D context sized to w x h
 * @param source  loaded image or canvas for the plate, or null
 * @param recipe  { format, face, position, colour, size, slant, bars,
 *                   treat, title, line }
 */
export function renderPoster(ctx, source, recipe, w, h) {
  const fmt = FORMATS[recipe.format] || FORMATS.poster
  const face = FACES[recipe.face] ? FACES[recipe.face] : FACES.condensed
  const font = faceFamily(recipe.face)
  const weight = face.weight
  ctx.save()
  ctx.fillStyle = INK
  ctx.fillRect(0, 0, w, h)

  // ── the plate, always full bleed ─────────────────────────────────────
  if (source) {
    const scale = Math.max(w / source.width, h / source.height)
    const dw = source.width * scale
    const dh = source.height * scale
    ctx.drawImage(source, (w - dw) / 2, (h - dh) / 2, dw, dh)
    const img = ctx.getImageData(0, 0, w, h)
    treatPixels(img.data, w, h, recipe.treat)
    ctx.putImageData(img, 0, 0)
  }

  // ── the words ────────────────────────────────────────────────────────
  //
  // No shadow, no keyline, no wash. Those were there so any colour would
  // survive any picture, which is exactly the safety that made everything
  // look the same. The colour is a choice now, and a bad one shows.
  const pad = Math.round(w * 0.055)
  const scale = Math.max(0.35, Math.min(1, Number(recipe.size) || 1))
  const measure = (w - pad * 2) * scale
  const colour = recipe.colour || '#ffffff'

  const titleLines = layOutTitle(
    ctx, recipe.title, measure, h * 0.62, font, weight, fmt.lines, h * fmt.maxTitle,
  )
  const lineSize = Math.round(w * (fmt === FORMATS.billboard ? 0.028 : 0.034) * (0.7 + scale * 0.3))
  const info = String(recipe.line || '').toUpperCase()

  const titleH = titleLines.reduce((sum, l) => sum + l.size * 0.86, 0)
  const infoH = info ? lineSize * 2.2 : 0

  ctx.textAlign = 'left'
  const anchor = pad

  // Split sends the details to the far end, which is what lets the middle
  // of the picture stay the picture.
  const at = POSITIONS[recipe.position] ? recipe.position : 'bottom'
  let y = at === 'top' || at === 'split' ? pad : h - pad - titleH - infoH

  const drawLine = (l) => {
    if (!l.text) return
    y += l.size * 0.78
    ctx.save()
    if (recipe.slant) {
      ctx.translate(anchor, y)
      ctx.transform(1, 0, -0.18, 1, 0, 0)
      ctx.translate(-anchor, -y)
    }
    ctx.font = `${weight} ${l.size}px ${font}`
    setTracking(ctx, l.size * face.tracking)

    if (recipe.bars) {
      ctx.fillStyle = colour
      ctx.fillRect(0, y - l.size * 0.72, Math.min(w, ctx.measureText(l.text).width + pad * 1.6), l.size * 0.86)
      ctx.fillStyle = pickContrast(colour)
    } else {
      ctx.fillStyle = colour
    }
    ctx.fillText(l.text, anchor, y)
    ctx.restore()
    y += l.size * 0.08
  }

  for (const l of titleLines) drawLine(l)

  if (info) {
    ctx.save()
    // The title gets fitted, and the details used to just get drawn: a long
    // enough line ran off the right edge and took the last word with it.
    // Shrink to the measure rather than wrap, because a detail line that
    // becomes two is a different design from the one that was chosen.
    let size = lineSize
    ctx.font = `${weight} ${size}px ${font}`
    setTracking(ctx, size * 0.2)
    const room = w - pad * 2
    const natural = ctx.measureText(info).width
    if (natural > room) {
      size = Math.max(size * 0.55, size * (room / natural))
      ctx.font = `${weight} ${size}px ${font}`
      setTracking(ctx, size * 0.2)
    }

    if (at === 'split') y = h - pad - size * 0.35
    else y += size * 1.15

    ctx.fillStyle = colour
    ctx.fillText(info, anchor, y)
    ctx.restore()
  }

  setTracking(ctx, 0)
  ctx.restore()
}
